import NoticeStage from "lib/core_components/NoticeStage";
import { useSubscription } from "lib/hooks/subscription";
import { NoticesStagesv2 } from "lib/types/graphQLEnums";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import { EntityActionParams } from "../../../lib/hooks/api/leads/useLeadEntityActions";
import { useRecordViewer } from "../../../lib/providers/RecordViewer";
import { sysBackgroundAlternative, sysTextSecondary } from "../../../lib/themes/colors";
import { Flex, Text } from "../../../styles/utility-components";
import { getNoticeDateValue } from "../../notices/utils";
import EntityCardContent from "../EntityCardContent";
import EntityTag from "../EntityTag";
import FindEntity from "../FindEntity";
import PaidTag from "../PaidTag";
import { SavedEntityCardContainer } from "../utils";

type NoticeEntity = {
  guid: string;
  name: string;
  stagev2: NoticesStagesv2 | null;
  expiryDate: string | null;
  closeDate: string | null;
  publishDate: string | null;
  procurementStageId: string;
};

export function SaveNoticeEntitiesCard({
  notices,
  entityUrl,
  removeEntity,
  contextSource,
}: {
  notices: NoticeEntity[];
  entityUrl: string;
  removeEntity: (entity: EntityActionParams) => void;
  contextSource: string;
}) {
  return (
    <Flex column gap={16}>
      {notices.map((notice) => (
        <SavedNoticeCard
          key={notice.guid}
          notice={notice}
          contextSource={contextSource}
          removeEntity={() =>
            removeEntity({
              entityType: "notice",
              entityId: notice.procurementStageId,
            })
          }
        />
      ))}
      <FindEntity entity="notice" url={entityUrl} isEmptyState={!notices.length} />
    </Flex>
  );
}

export function SavedNoticeCard({
  notice,
  removeEntity,
  contextSource,
}: {
  notice: NoticeEntity;
  removeEntity: () => void;
  contextSource: string;
}) {
  const { viewRecord } = useRecordViewer();

  return (
    <SavedEntityCardContainer
      onClick={() => viewRecord(notice.guid, { "Context source": contextSource })}
      aria-label="Notice card"
    >
      <EntityCardContent
        header={notice.stagev2 && <NoticeStage stage={notice.stagev2} />}
        entity="notice"
        removeEntity={removeEntity}
      >
        <NoticeCard notice={notice} />
      </EntityCardContent>
    </SavedEntityCardContainer>
  );
}

export function NoticeCard({ notice }: { notice: NoticeEntity }) {
  const subscription = useSubscription();
  const hasAccess = shouldHaveAwardAccess(notice) ? subscription.hasDataTypes("AWARDS") : true;

  const noticeDateInRelativeFormat = getNoticeDateInRelativeFormat(notice);

  return (
    <Flex column gap={16} width="100%">
      <Text>{notice.name || "Untitled Notice"}</Text>
      <Flex gap={8} flexWrap="wrap">
        {noticeDateInRelativeFormat && (
          <EntityTag
            styles={{
              backgroundColor: sysBackgroundAlternative,
              color: sysTextSecondary,
            }}
            title={noticeDateInRelativeFormat}
          />
        )}
        {!hasAccess && <PaidTag />}
      </Flex>
    </Flex>
  );
}

function getNoticeDateInRelativeFormat(notice: NoticeEntity) {
  const date = getNoticeDateValue(notice);
  if (!date) return;

  const humanReadableDate = toHumanRelativeDateFromNow(date);

  let actionText;
  switch (notice.stagev2) {
    case NoticesStagesv2.AwardedContract:
    case NoticesStagesv2.ExpiredContract:
    case NoticesStagesv2.CancelledAward:
      actionText = humanReadableDate.includes("ago") ? "Expired" : "Expiring";
      break;
    case NoticesStagesv2.OpenTender:
    case NoticesStagesv2.ClosedTender:
    case NoticesStagesv2.CancelledTender:
    case NoticesStagesv2.UnknownTender:
      actionText = humanReadableDate.includes("ago") ? "Closed" : "Closing";
      break;
    default:
      actionText = humanReadableDate.includes("ago") ? "Published" : "Publishing";
      break;
  }

  return `${actionText} ${humanReadableDate}`;
}

function shouldHaveAwardAccess(notice: NoticeEntity) {
  if (!notice.stagev2) return false;
  return [
    NoticesStagesv2.AwardedContract,
    NoticesStagesv2.ExpiredContract,
    NoticesStagesv2.CancelledAward,
  ].includes(notice.stagev2);
}
